import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

const Ctx = createContext(null)

export function StepSubmitProvider({ children }) {
  const handlerRef = useRef(null)
  const tokenRef = useRef(0)
  const [errorCount, setErrorCountState] = useState(0)
  const [pulseKey, setPulseKey] = useState(0)

  // Token-based ownership. Each step that registers gets a fresh token; the
  // ref records which token currently owns the handler. A step's cleanup only
  // clears the handler if it STILL owns it — so when AnimatePresence keeps the
  // exiting step mounted during the crossfade, its late setHandler(null) no
  // longer clobbers the incoming step's handler (the dead-Continue race).
  const setHandler = useCallback((fn) => {
    if (fn == null) return tokenRef.current
    const token = tokenRef.current + 1
    tokenRef.current = token
    handlerRef.current = fn
    return token
  }, [])

  const clearHandler = useCallback((token) => {
    if (token === tokenRef.current) {
      handlerRef.current = null
    }
  }, [])

  const runHandler = useCallback(() => {
    if (typeof handlerRef.current === 'function') {
      handlerRef.current()
      return true
    }
    return false
  }, [])

  const reportErrorCount = useCallback((n) => {
    setErrorCountState(Number(n) || 0)
  }, [])

  const pulse = useCallback(() => {
    setPulseKey((k) => k + 1)
  }, [])

  const focusFirstError = useCallback(() => {
    if (typeof document === 'undefined') return
    const candidates = Array.from(
      document.querySelectorAll(
        '.field-input.has-error, .row-input.has-error, [aria-invalid="true"], .row-input-error, .field-error',
      ),
    )
    if (candidates.length === 0) return
    const topmost = candidates.reduce((best, el) => {
      const t = el.getBoundingClientRect().top
      const bt = best.getBoundingClientRect().top
      return t < bt ? el : best
    }, candidates[0])
    const scroller = document.querySelector('.form-content')
    if (scroller) {
      const top =
        topmost.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop -
        80
      scroller.scrollTo({ top, behavior: 'smooth' })
    } else {
      topmost.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    let focusTarget = topmost
    if (typeof focusTarget.focus !== 'function' || focusTarget.tagName === 'DIV') {
      const row = focusTarget.closest('.entry-row, .row-cell-stack, .step-section, .step-body')
      const input = row?.querySelector(
        'input:not([type="hidden"]):not([disabled]):not([readonly]), select, textarea',
      )
      if (input) focusTarget = input
    }
    setTimeout(() => {
      if (typeof focusTarget.focus === 'function') {
        focusTarget.focus({ preventScroll: true })
      }
    }, 300)
  }, [])

  const value = useMemo(
    () => ({
      setHandler,
      clearHandler,
      runHandler,
      errorCount,
      reportErrorCount,
      pulseKey,
      pulse,
      focusFirstError,
    }),
    [
      setHandler,
      clearHandler,
      runHandler,
      errorCount,
      reportErrorCount,
      pulseKey,
      pulse,
      focusFirstError,
    ],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useStepSubmit() {
  const ctx = useContext(Ctx)
  if (!ctx)
    throw new Error('useStepSubmit must be used inside StepSubmitProvider')
  return ctx
}
